import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Chip } from '@/components/ui/Chip';
import { useStore } from '@/providers/StoreProvider';
import { cn } from '@/lib/cn';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface SelectedNeighborhoodChipsProps {
  mobile?: boolean;
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  includeBuilding?: boolean;
  className?: string;
}

export function SelectedNeighborhoodChips({
  mobile = false,
  selectedNeighborhoodIds: propSelectedIds,
  onToggleNeighborhood: propToggleNeighborhood,
  onToggleNeighborhoodWithChildren: propToggleNeighborhoodWithChildren,
  onToggleBorough: propToggleBorough,
  includeBuilding = true,
  className,
}: SelectedNeighborhoodChipsProps) {
  const analytics = useAnalytics();
  const containerRef = useRef<HTMLDivElement>(null);

  const storeNeighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const allNeighborhoods = useStore(s => s.neighborhoods.neighborhoods);
  const accordion = useStore(s => s.neighborhoods.neighborhoodAccordionData);
  const building = useStore(s => s.listingsFilters.building);
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const storeToggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );
  const removeNeighborhoods = useStore(s => s.listingsFilters.removeNeighborhoods);
  const storeToggleBorough = useStore(s => s.listingsFilters.toggleBorough);

  const neighborhoodIds = propSelectedIds ?? storeNeighborhoodIds;
  const toggleNeighborhoodWithChildren =
    propToggleNeighborhoodWithChildren ?? storeToggleNeighborhoodWithChildren;
  const toggleBorough = propToggleBorough ?? storeToggleBorough;

  const handleRemoveNeighborhood = useCallback(
    (id: string) => {
      if (!id) return;

      const n = allNeighborhoods.find(n => n.id === id);
      analytics.track('listings_filter_neighborhood_unselect', {
        neighborhoodId: id,
        ...(n?.name ? { neighborhoodName: n.name } : {}),
      });

      if (propToggleNeighborhood) {
        propToggleNeighborhood(id);
      } else {
        removeNeighborhoods([id]);
      }
    },
    [allNeighborhoods, analytics, propToggleNeighborhood, removeNeighborhoods],
  );

  const hasNeighborhoods = Array.isArray(neighborhoodIds) && neighborhoodIds.length > 0;
  const hasBuilding = includeBuilding && building?.slug;

  const byId = new Map(allNeighborhoods.map(n => [n.id, n] as const));

  const selectedSet = new Set(neighborhoodIds);
  const boroughAllIds: Record<string, string[]> = {};
  const fullySelectedBoroughs = new Set<string>();
  if (accordion && typeof accordion === 'object') {
    for (const [borough, entry] of Object.entries(accordion)) {
      const ids: string[] = [];
      for (const p of entry.parents || []) {
        ids.push(p.id);
      }
      for (const group of Object.values(entry.childrenByParent || {})) {
        for (const c of group) {
          ids.push(c.id);
        }
      }
      boroughAllIds[borough] = ids;
      if (ids.length > 0 && ids.every(id => selectedSet.has(id))) {
        fullySelectedBoroughs.add(borough);
      }
    }
  }

  const scrollKey = useMemo(() => {
    if (!mobile) return '';
    const ids = Array.isArray(neighborhoodIds) ? neighborhoodIds : [];
    return `${building?.slug ?? ''}|${ids.join(',')}`;
  }, [building?.slug, includeBuilding, mobile, neighborhoodIds]);

  useEffect(() => {
    if (!mobile) return;
    const el = containerRef.current;
    if (!el) return;
    // Ensure newly-added chips are visible by scrolling to the end.
    el.scrollLeft = el.scrollWidth;
  }, [mobile, scrollKey]);

  // If no neighborhoods and no building to show, return null
  if (!hasNeighborhoods && !hasBuilding) return null;

  const emittedBorough = new Set<string>();
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  for (const id of neighborhoodIds) {
    const n = byId.get(id);
    const borough = n?.borough;
    if (borough && fullySelectedBoroughs.has(borough)) {
      if (!emittedBorough.has(borough)) {
        emittedBorough.add(borough);
        chips.push({
          key: `borough-${borough}`,
          label: borough,
          onRemove: () => {
            analytics.track('listings_filter_borough_unselect', { boroughName: borough });
            toggleBorough(borough);
          },
        });
      }
      continue;
    }

    const isChildHidden = n?.parentId && neighborhoodIds.includes(n.parentId);
    if (isChildHidden) continue;

    const label = n?.name || id;
    chips.push({
      key: id,
      label,
      onRemove: () => {
        if (n?.childrenIds && n.childrenIds.length > 0) {
          analytics.track('listings_filter_neighborhood_unselect', {
            neighborhoodId: id,
            neighborhoodName: n?.name,
          });
          toggleNeighborhoodWithChildren(id);
        } else {
          handleRemoveNeighborhood(id);
        }
      },
    });
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex gap-2',
        mobile
          ? 'overflow-x-auto overflow-y-hidden flex-nowrap [&::-webkit-scrollbar]:hidden'
          : 'flex-wrap',
        className,
      )}
      style={
        mobile
          ? {
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }
          : undefined
      }
    >
      {hasBuilding && (
        <Chip
          className="bg-active shrink-0"
          label={building?.address ?? building?.slug ?? ''}
          onRemove={() => resetBuilding()}
        />
      )}
      {hasNeighborhoods &&
        chips.map(chip => (
          <Chip
            key={chip.key}
            className="bg-active shrink-0"
            label={chip.label}
            onRemove={chip.onRemove}
          />
        ))}
    </div>
  );
}

export default SelectedNeighborhoodChips;
